import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Avatar as AntAvatar } from 'antd';
import { AvatarSize } from 'antd/es/avatar/AvatarContext';
import cs from 'classnames';

import useErrorCatch from 'hooks/useErrorCatch';

import fetchTextWithAuth from 'utils/api/fetchTextWithAuth';
import normalizeUrlEndpoint from 'utils/api/normalizeUrlEndpoint';

import { RESOURCE } from 'constants/endpoints';
import { JOBSEEKER_AVATAR_SIZE_PX } from 'constants/uiSettings';

import { ReactComponent as DefaultProfilePhotoIcon } from 'assets/icons/default-profile-photo.svg';

import styles from './JobseekerAvatar.module.scss';

type CacheEntry = { value: string; createdAt: number };
const SIGNED_URL_CACHE_TTL_MS = 2_000;
const signedUrlCache = new Map<string, CacheEntry>();
const signedUrlInFlight = new Map<string, Promise<string>>();

interface AvatarProps {
  size?: AvatarSize;
  photoUrlEndpoint?: string | null;
  photoId?: string;
}

const JobseekerAvatar: FC<AvatarProps> = ({
  size = JOBSEEKER_AVATAR_SIZE_PX,
  photoUrlEndpoint = null,
  photoId
}) => {
  const { t } = useTranslation();
  const [srcUrl, setSrcUrl] = useState<string | null>(null);
  const catchError = useErrorCatch();

  const resolveSignedUrl = async (endpoint: string): Promise<string> => {
    const cached = signedUrlCache.get(endpoint);
    const now = Date.now();
    if (cached && now - cached.createdAt < SIGNED_URL_CACHE_TTL_MS) {
      return cached.value;
    }

    const inFlight = signedUrlInFlight.get(endpoint);
    if (inFlight) {
      return inFlight;
    }

    const signedUrlPromise = fetchTextWithAuth(endpoint)
      .then((value) => {
        signedUrlCache.set(endpoint, { value, createdAt: Date.now() });
        return value;
      })
      .finally(() => {
        signedUrlInFlight.delete(endpoint);
      });

    signedUrlInFlight.set(endpoint, signedUrlPromise);
    return signedUrlPromise;
  };

  const getPhoto = async (
    photoUrlEndpoint: string | null,
    photoId?: string
  ): Promise<void> => {
    const endpointRaw =
      photoUrlEndpoint || (photoId ? `${RESOURCE}/${photoId}/url` : null);

    if (!endpointRaw) {
      setSrcUrl(null);
      return;
    }

    try {
      const endpoint = normalizeUrlEndpoint(endpointRaw);
      const signedUrl = await resolveSignedUrl(endpoint);
      setSrcUrl(signedUrl);
    } catch (error) {
      catchError({
        error,
        default: {
          description: t('error.message.loadJobseekerPhoto')
        }
      });
    }
  };

  useEffect(() => {
    getPhoto(photoUrlEndpoint, photoId);
  }, [photoUrlEndpoint, photoId]);

  return (
    <AntAvatar
      size={size}
      src={srcUrl || <DefaultProfilePhotoIcon />}
      className={cs(styles.wrapper, { [styles.wrapperDefault]: !srcUrl })}
    />
  );
};

export default JobseekerAvatar;
